<script context="module" lang="ts">
    import { format as formatDate, fromUnixTime as unixToDate } from 'date-fns';
    // What a forecast day is. The name stored on results (a_name) isn't updated when the optimizer changes a day, so go by its state and XSS.
    export type DayKind = 'event' | 'rest' | 'high' | 'low' | 'training'
    export const dayKind = (day: any | null): DayKind => {
        const state: string[] = day?.state ?? []
        if (state.some(s => s.startsWith('event'))) return 'event'
        if (!day || (day.xss ?? 0) <= 0) return 'rest'
        if (state.includes('high_intensity')) return 'high'
        if (state.includes('low_intensity')) return 'low'
        return 'training'
    }

    export const dayKey = (day: any) => formatDate(unixToDate(day.t), 'yyyy-MM-dd')
    const dayHours = (d: any) => d.adu > 0 ? d.adu : d.duration ?? 0
    // Ignore tiny differences (rounding, a few minutes) so only real changes count.
    export const isDayChanged = (b: any | null, a: any | null) => {
        if (dayKind(b) !== dayKind(a)) return true
        if (dayKind(a) === 'rest' || !b || !a) return false
        return Math.abs((b.xss ?? 0) - (a.xss ?? 0)) >= 1
            || Math.abs(dayHours(b) - dayHours(a)) * 60 >= 1
            || Math.abs(b.t - a.t) >= 5 * 60
    }
    /** Changes worth a look: a rest day added or removed, a Low day that became High (or the reverse), or a day's XSS that moved by more than 5%. */
    export const NOTABLE_XSS_CHANGE = 0.05
    export const isNotableChange = (b: any | null, a: any | null) => {
        const kb = dayKind(b), ka = dayKind(a)
        if ((kb === 'rest') !== (ka === 'rest')) return true
        if ((kb === 'low' && ka === 'high') || (kb === 'high' && ka === 'low')) return true
        const xb = b?.xss ?? 0, xa = a?.xss ?? 0
        return xb > 0 && Math.abs(xa - xb) / xb > NOTABLE_XSS_CHANGE
    }
    /** Dates ('yyyy-MM-dd') of the days an adapted forecast changes, comparing the current plan's days with the adapted ones. */
    export const changedDayKeys = (before: any[], after: any[]): string[] => {
        // Every entry of a date is kept (a forecast may hold more than one for a day) and compared in order.
        const byDay = new Map<string, { before: any[], after: any[] }>()
        for (const [side, days] of [['before', before], ['after', after]] as const) {
            for (const day of days ?? []) {
                const row = byDay.get(dayKey(day)) ?? { before: [], after: [] }
                row[side].push(day)
                byDay.set(dayKey(day), row)
            }
        }
        const changed = (row: { before: any[], after: any[] }) =>
            Array.from({ length: Math.max(row.before.length, row.after.length) }, (_, i) => isDayChanged(row.before[i] ?? null, row.after[i] ?? null)).some(Boolean)
        return [...byDay.entries()].filter(([, row]) => changed(row)).map(([key]) => key)
    }
</script>

<script lang="ts">
    import { format, fromUnixTime } from 'date-fns';
    import Matchstick from '@/Common/Matchstick.svelte';
    import XSSCircle from '../TrainingForecast/XSSCircle.svelte';
    import { cn } from '@/lib/utils';
    import { convertSeconds } from '@/Util/utils';

    // A day of forecast results (null = nothing planned, shown as a rest day like the planner does).
    export let day: any | null
    export let dim = false

    $: kind = dayKind(day)
    $: isEvent = kind === 'event'
    $: isRest = kind === 'rest'
    $: xss = day?.xss ?? 0
    $: name = { event: day?.a_name ?? 'Event', rest: 'Rest', high: 'High Intensity Training', low: 'Low Intensity Training', training: 'Training' }[kind]
    $: duration = (day?.adu > 0 ? day.adu : day?.duration ?? 0) * 3600
    $: xssDetails = { xss, xlss: day?.xlss ?? 0, xhss: day?.xhss ?? 0, xpss: day?.xpss ?? 0 }
</script>

<!-- Read-only: styled like the planner's forecast card, without its drag, menu or dialogs. -->
<div class={cn('flex flex-row rounded-sm border bg-muted min-w-0', isRest ? 'bg-transparent border-dashed' : '', dim ? 'border-slate-200' : 'border-border')}>
    <Matchstick width={4} xss={xss} xlss={day?.xlss ?? 0} xhss={day?.xhss ?? 0} xpss={day?.xpss ?? 0} {isRest} />
    <div class="flex flex-col gap-1 p-1 flex-1 min-w-0">
        <div class="flex flex-row gap-1 items-center">
            {#if isEvent}
                <i class="fa-light fa-bullseye-arrow text-black"></i>
            {:else if isRest}
                <i class="fa-light fa-sheep text-black"></i>
            {:else}
                <i class="fa-light fa-bicycle text-black"></i>
            {/if}
            <span class="text-xs font-bold truncate">{name}</span>
        </div>
        <span class="self-start rounded-sm bg-[#b4e4ee] px-1.5 text-[10px] leading-4 text-slate-700">Forecast</span>
        {#if !isRest && !isEvent}
            <div class="flex flex-row items-center gap-2">
                <XSSCircle primaryXSSDetails={xssDetails} primaryTitle="Forecasted" />
                <div class="flex flex-1 flex-row justify-end gap-4">
                    <div class="flex flex-col">
                        <span class="text-[10px] font-medium uppercase text-muted-foreground">Duration</span>
                        <span class="text-[14px] font-medium">{convertSeconds(duration)}</span>
                    </div>
                    <div class="flex flex-col">
                        <span class="text-[10px] font-medium uppercase text-muted-foreground">Start Time</span>
                        <span class="text-[14px] font-medium">{format(fromUnixTime(day.t), 'hh:mm aa')}</span>
                    </div>
                </div>
            </div>
        {/if}
    </div>
</div>
